import { useCallback, useState, useEffect } from 'react'

const STORAGE_KEY = 'makemerry_traveling_plan'
const PLANS_STORAGE_KEY = 'makemerry_plans'

// Predefined color palette for plans
export const PLAN_COLORS = [
    '#3B82F6', // Blue
    '#EF4444', // Red
    '#10B981', // Green
    '#F59E0B', // Amber
    '#8B5CF6', // Purple
    '#EC4899', // Pink
    '#06B6D4', // Cyan
    '#F97316', // Orange
]

function loadPlanItems() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY)
        return stored ? JSON.parse(stored) : []
    } catch (error) {
        console.error('Failed to load traveling plan:', error)
        return []
    }
}

function savePlanItems(items) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
    } catch (error) {
        console.error('Failed to save traveling plan:', error)
    }
}

function loadPlans() {
    try {
        const stored = localStorage.getItem(PLANS_STORAGE_KEY)
        const plans = stored ? JSON.parse(stored) : []

        // Create default plan if no plans exist
        if (plans.length === 0) {
            const defaultPlan = {
                id: 'plan_' + Date.now(),
                name: 'My Plan',
                color: PLAN_COLORS[0],
                createdAt: Date.now()
            }
            return [defaultPlan]
        }

        return plans
    } catch (error) {
        console.error('Failed to load plans:', error)
        return [{
            id: 'plan_' + Date.now(),
            name: 'My Plan',
            color: PLAN_COLORS[0],
            createdAt: Date.now()
        }]
    }
}

function savePlans(plans) {
    try {
        localStorage.setItem(PLANS_STORAGE_KEY, JSON.stringify(plans))
    } catch (error) {
        console.error('Failed to save plans:', error)
    }
}

export function useTravelingPlan() {
    const [planItems, setPlanItems] = useState(() => loadPlanItems())
    const [plans, setPlans] = useState(() => loadPlans())

    // Save to localStorage whenever plan items change
    useEffect(() => {
        savePlanItems(planItems)
    }, [planItems])

    // Save to localStorage whenever plans change
    useEffect(() => {
        savePlans(plans)
    }, [plans])

    // Create a new plan
    const createPlan = useCallback((name, color) => {
        const newPlan = {
            id: 'plan_' + Date.now(),
            name,
            color,
            createdAt: Date.now()
        }
        setPlans(prev => [...prev, newPlan])
        return newPlan
    }, [])

    // Update an existing plan
    const updatePlan = useCallback((planId, updates) => {
        setPlans(prev => prev.map(plan =>
            plan.id === planId ? { ...plan, ...updates } : plan
        ))
    }, [])

    // Delete a plan and all its items
    const deletePlan = useCallback((planId) => {
        setPlans(prev => prev.filter(plan => plan.id !== planId))
        setPlanItems(prev => prev.filter(item => item.planId !== planId))
    }, [])

    const addToPlan = useCallback((item, date, planId) => {
        setPlanItems(prev => {
            // Check if already in this specific plan for this date
            const existingIndex = prev.findIndex(
                planItem => planItem.id === item.id &&
                           planItem.plannedDate === date &&
                           planItem.planId === planId
            )

            if (existingIndex !== -1) {
                return prev // Already in this plan for this date
            }

            return [
                ...prev,
                {
                    ...item,
                    planId,
                    plannedDate: date,
                    addedAt: Date.now()
                }
            ]
        })
    }, [])

    const removeFromPlan = useCallback((itemId, planId, date) => {
        setPlanItems(prev =>
            prev.filter(item => !(
                item.id === itemId &&
                item.planId === planId &&
                item.plannedDate === date
            ))
        )
    }, [])

    const togglePlanItem = useCallback((item, date, planId) => {
        setPlanItems(prev => {
            const existingIndex = prev.findIndex(
                planItem => planItem.id === item.id &&
                           planItem.plannedDate === date &&
                           planItem.planId === planId
            )

            if (existingIndex !== -1) {
                // Remove from plan
                return prev.filter((_, index) => index !== existingIndex)
            }

            // Add to plan
            return [
                ...prev,
                {
                    ...item,
                    planId,
                    plannedDate: date,
                    addedAt: Date.now()
                }
            ]
        })
    }, [])

    const isInPlan = useCallback((itemId, date, planId) => {
        return planItems.some(
            item => item.id === itemId &&
                   item.plannedDate === date &&
                   item.planId === planId
        )
    }, [planItems])

    // Check which plans contain a specific item for a specific date
    const getItemPlans = useCallback((itemId, date) => {
        return planItems
            .filter(item => item.id === itemId && item.plannedDate === date)
            .map(item => item.planId)
    }, [planItems])

    const clearPlan = useCallback((planId) => {
        if (planId) {
            // Clear specific plan
            setPlanItems(prev => prev.filter(item => item.planId !== planId))
        } else {
            // Clear all plans
            setPlanItems([])
        }
    }, [])

    // Group items by date for a specific plan
    const planByDate = useCallback((planId) => {
        const filtered = planId
            ? planItems.filter(item => item.planId === planId)
            : planItems

        const grouped = {}

        filtered.forEach(item => {
            if (!grouped[item.plannedDate]) {
                grouped[item.plannedDate] = []
            }
            grouped[item.plannedDate].push(item)
        })

        // Sort dates
        const sortedDates = Object.keys(grouped).sort()

        return sortedDates.map(date => ({
            date,
            items: grouped[date].sort((a, b) => a.addedAt - b.addedAt) // Sort by when added
        }))
    }, [planItems])

    // Get plan by ID
    const getPlan = useCallback((planId) => {
        return plans.find(plan => plan.id === planId)
    }, [plans])

    return {
        // Plan items
        planItems,
        planByDate,
        addToPlan,
        removeFromPlan,
        togglePlanItem,
        isInPlan,
        getItemPlans,
        clearPlan,

        // Plans management
        plans,
        createPlan,
        updatePlan,
        deletePlan,
        getPlan,
    }
}
